import * as React from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/styles";
import { Card, CardHeader, Divider, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ExcelIcon from "mdi-material-ui/FileExcel";
import ViewIcon from "@material-ui/icons/RemoveRedEye";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { WithReduxForm, NumberInput, Button, minValue, maxValue, required, BackButton } from "jazasoft";
import Table from "jazasoft/lib/mui/components/Table";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";

import MuiTable from "../../components/MuiTable";
import Tabs from "../../components/tab/Tabs";
import SubTab from "../../components/tab/SubTab";

import { useDeepMemo, useSafeState, useSetting } from "../../hooks";
import { getDistinctValues, inputOptions, isEmpty, isFalse, isTrue, toFixed, toFixedSigned } from "../../utils/helpers";

import useGroupingView from "./useGroupingView";
import { ShadeRowRenderer } from "./ShadeRowRenderer";
import GroupOptionsTable from "./GroupOptionsTable";
import { OState } from "../../utils/types";
import { getDeviation, shrinkageValue } from "./groupingHelper";
import RollDrawer from "./RollDrawer";

import hasPrivilege from "../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../utils/privilege";
import SettingKeys from "../../utils/SettingKeys";
import PageFooter from "jazasoft/lib/mui/layout/PageFooter";
import { UOM } from "../../constants";
import { convert } from "../../helpers/unitConversionHelper";
import ExternalRollDrawer from "./ExternalRollDrawer";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const cbGroupSort = (a, b) => parseInt(a.name.slice(1)) - parseInt(b.name.slice(1));

const groupingSettingFields = ["warpInterval", "weftInterval", "widthWastePercent", "fabAllocPlusValue", "fabAllocMinusValue", "minRollSplitQty"];

const getGroupFabricQty = (group, uom, conversionFactorList) => {
  if (isEmpty(group) || isEmpty(uom)) return "";
  return (
    `${toFixed(group.totalLength)} m` +
    (uom === UOM.METER.value
      ? ""
      : uom === UOM.KILOGRAM.value
      ? ` (${toFixed(group.totalWeight)} kg)`
      : ` (${toFixed(convert({ input: group.totalLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList }))} ${UomCodeFromValue[uom]})`)
  );
};

const shrOptionColumns = [
  { dataKey: "warp", title: "Warp" },
  { dataKey: "weft", title: "Weft" },
  {
    dataKey: "fabricQty",
    title: "Fabric",
    align: "center"
  },
  { dataKey: "deviations", title: "Deviations" }
];

const widthOptionColumns = [
  { dataKey: "width", title: "Width (cm)" },
  { dataKey: "fabricQty", title: "Fabric" }
];

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    padding: "1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  }
});

const GroupingView = props => {
  const classes = useStyles();
  const { roles = [], hasAccess, i18nKey } = props;
  const {
    busy,
    version,
    conversionFactorList,
    errorMessage,
    selectedKeys,
    selectedKey,
    order,
    flowInfoColumns,
    fabricInfoColumns,
    shadeColumns,
    flowInfoRows,
    fabricInfoRows,
    shadeRows,
    tabIdx,
    shrinkageOptions,
    widthOptionState,
    groupOrder,
    groupList,
    setTabIdx,
    setSelectedKey,
    minRollSplitLength,
    ledgerList,
    extRollList,
    extDrawerState,
    setExtDrawerState,
    onSelect,
    runShrinkageGrouping,
    runFinalGrouping,
    updatePattern,
    handleSelection,
    onDownload,
    onSave,
    onDelete,
    onCancel,
    rollInfoList
  } = useGroupingView(props);

  const [drawerState, setDrawerState] = useSafeState({ open: false, type: null, group: null, groupList: [], flatView: false });

  const { widthOptions, finalOption } = widthOptionState || {};
  const enablePatternNumberOverrideVal = useSetting(SettingKeys.ENABLE_PATTERN_NUMBER_OVERRIDE);
  const fabricAllocPositiveTolerance = useSetting(SettingKeys.FABRIC_ALLOCATION_POSITIVE_TOLERANCE);
  const fabricAllocNegativeTolerance = useSetting(SettingKeys.FABRIC_ALLOCATION_NEGATIVE_TOLERANCE);
  const enablePatternNumberOverride = isTrue(enablePatternNumberOverrideVal);
  const formValues = useSelector(state => state?.form && state.form["record-form"]?.values);

  const onRowClick = row => {
    if (tabIdx === 1) return;
    if (isFalse(row.isParent)) {
      setSelectedKey(row.key);
    }
  };

  const shrGroupColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "name", title: "Group" },
      {
        dataKey: "warpSystem",
        title: "Length Pattern \n(System)",
        align: "center",
        render: value => (isEmpty(value) ? "NIL" : value)
      },
      {
        dataKey: "weftSystem",
        title: "Width Pattern \n(System)",
        align: "center",
        render: value => (isEmpty(value) ? "NIL" : value)
      },
      {
        dataKey: "warpManual",
        title: "Length Pattern \n(Manual)",
        align: "center",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "NIL" : `${toFixedSigned(value, 1)}%`)
      },
      {
        dataKey: "weftManual",
        title: "Width Pattern \n(Manual)",
        align: "center",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "NIL" : `${toFixedSigned(value, 1)}%`)
      },
      { dataKey: "patternNumber", title: "Pattern No.", align: "center", inputType: enablePatternNumberOverride ? "text-input" : undefined },
      { dataKey: "fabricQty", title: "Fabric Qty", align: "right" }
      // { dataKey: "deviations", title: "Deviations", linkPath: row => {} }
    ];
    return cols;
  }, [enablePatternNumberOverride]);

  const uom =
    tabIdx === 0
      ? flowInfoRows.find(e => e.key === selectedKey)?.uom
      : flowInfoRows.filter(row => selectedKeys.includes(row.key) && !row.isParent)[0]?.uom;

  const shrGroupRows = useDeepMemo(() => {
    return groupList.sort(cbGroupSort).map(group => ({
      ...group,
      warpSystem: shrinkageValue(group, "warpMean"),
      weftSystem: shrinkageValue(group, "weftMean"),
      warpManual: group?.warpPattern,
      weftManual: group?.weftPattern,
      fabricQty: getGroupFabricQty(group, uom, conversionFactorList),
      deviations: getDeviation(group)
    }));
  }, [groupList, uom, conversionFactorList]);

  const finalOptionColumns = useDeepMemo(() => {
    if (isEmpty(finalOption?.groupList)) return [];
    const isWashProgram = finalOption.groupList.some(group => !isEmpty(group.warpPattern) && !isEmpty(group.weftPattern));
    let cols = [
      { dataKey: "name", title: "Group" },
      { dataKey: "width", title: "Width (cm)" },
      { dataKey: "warpSystem", title: "Length Pattern \n(System)", align: "center" },
      { dataKey: "weftSystem", title: "Width Pattern \n(System)", align: "center" },
      { dataKey: "warpManual", title: "Length Pattern \n(Manual)", align: "center" },
      { dataKey: "weftManual", title: "Width Pattern \n(Manual)", align: "center" },
      { dataKey: "patternNumber", title: "Pattern No.", align: "center" },
      { dataKey: "csvValue", title: "CSV", align: "center" },
      { dataKey: "fabricQty", title: "Fabric Qty", align: "right" },
      {
        dataKey: "wastage",
        title: "Wastage",
        rowSpan: ({ rowIdx }) => (rowIdx === 0 && finalOption.groupList ? finalOption.groupList.length : 0)
      }
    ];
    if (!isWashProgram) {
      cols = cols.filter(c => !["warpSystem", "weftSystem", "warpManual", "weftManual", "patternNumber"].includes(c.dataKey));
    }
    return cols;
  }, [finalOption]);

  const finalGroupRows = useDeepMemo(() => {
    return groupList
      .filter(group => group.type === "FINAL")
      .sort(cbGroupSort)
      .map(group => ({
        ...group,
        warpSystem: shrinkageValue(group, "warpMean"),
        weftSystem: shrinkageValue(group, "weftMean"),
        warpManual: group?.warpPattern,
        weftManual: group?.weftPattern,
        width: `${group.widthAxis} (${toFixed(group.widthAxis, 1)} - ${toFixed(group.widthAxis + group.widthVariance, 1)})`,
        fabricQty: getGroupFabricQty(group, uom, conversionFactorList),
        deviations: getDeviation(group)
      }));
  }, [groupList, uom, conversionFactorList]);

  const isWashProgram = groupList.some(group => !isEmpty(group.warpPattern) && !isEmpty(group.weftPattern));
  const finalGroupColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "name", title: "Group" },
      { dataKey: "width", title: "Width (cm)" },
      {
        dataKey: "warpSystem",
        title: "Length Pattern \n(System)",
        align: "center",
        render: value => (isEmpty(value) ? "NIL" : value)
      },
      {
        dataKey: "weftSystem",
        title: "Width Pattern \n(System)",
        align: "center",
        render: value => (isEmpty(value) ? "NIL" : value)
      },
      {
        dataKey: "warpManual",
        title: "Length Pattern \n(Manual)",
        align: "center",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "NIL" : `${toFixedSigned(value, 1)}%`)
      },
      {
        dataKey: "weftManual",
        title: "Width Pattern \n(Manual)",
        align: "center",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "NIL" : `${toFixedSigned(value, 1)}%`)
      },
      { dataKey: "patternNumber", title: "Pattern No.", align: "center", inputType: enablePatternNumberOverride ? "text-input" : undefined },
      { dataKey: "csv", title: "CSV", align: "center" },
      { dataKey: "fabricQty", title: "Fabric Qty", align: "right" }
      // { dataKey: "deviations", title: "Deviations", linkPath: row => {} }
    ];
    if (!isWashProgram) {
      cols = cols.filter(c => !["warpSystem", "weftSystem", "warpManual", "weftManual", "patternNumber", "deviations"].includes(c.dataKey));
    }
    return cols;
  }, [isWashProgram, enablePatternNumberOverride]);

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "download") {
      onDownload(onActionComplete, rows);
    } else if (action === "delete") {
      onDelete(onActionComplete, rows);
    }
  };

  const handleDeviationClick = ({ record }) => {
    setDrawerState({ open: true, type: "deviation", group: record });
  };

  const handleViewClick = groupList => {
    setDrawerState({ open: true, type: "rollInfo", groupList });
  };

  const footerActions = useDeepMemo(() => {
    if (isEmpty(groupOrder)) return [];
    let actions = [
      {
        name: "delete",
        tooltip: "Delete",
        icon: <DeleteIcon />,
        options: { variant: "outlined", style: { color: "red", borderColor: "red" } }
      },
      {
        name: "download",
        tooltip: "Download",
        icon: <ExcelIcon />,
        options: { variant: "outlined", style: { color: "green", borderColor: "green" } }
      },
      { name: "edit", tooltip: "Update", icon: <EditIcon />, options: { variant: "outlined", color: "primary" } }
    ];
    if (![OState.SHRINKAGE_GROUPING_COMPLETED.state, OState.FINAL_GROUPING_COMPLETED.state].includes(groupOrder.state)) {
      actions = actions.filter(e => e.name !== "delete");
    } else {
      if (
        OState.SHRINKAGE_GROUPING_COMPLETED.state === groupOrder.state &&
        !hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.DELETE, "shrinkage")
      ) {
        actions = actions.filter(e => e.name !== "delete");
      } else if (
        OState.FINAL_GROUPING_COMPLETED.state === groupOrder.state &&
        !hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.DELETE, "final")
      ) {
        actions = actions.filter(e => e.name !== "delete");
      }
    }

    if (
      (!isWashProgram || groupOrder.stateValue > OState.FINAL_GROUPING_COMPLETED.stateValue) &&
      !hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.UPDATE)
    ) {
      actions = actions.filter(e => e.name !== "edit");
    }
    return actions;
  }, [groupOrder, isWashProgram, roles, hasAccess]);

  const settings = !isEmpty(groupOrder?.orderConfig?.groupingSetting) ? JSON.parse(groupOrder.orderConfig.groupingSetting) : {};

  // const defaultExpanded = flowInfoRows.filter(fi => isEmpty(fi.parentKey)).length === 1;
  const defaultExpanded = true;

  // const parentKeys = getDistinctValues(flowInfoRows.filter(fi => !isEmpty(fi.parentKey)).map(fi => fi.parentKey));
  const enableRunShrinkage = tabIdx === 1 && isEmpty(errorMessage) && !isEmpty(fabricInfoRows) && fabricInfoRows.some(row => row.shrinkageReady > 0);
  const enableRunFinalBtn =
    tabIdx === 1
      ? !enableRunShrinkage && isEmpty(errorMessage) && !isEmpty(fabricInfoRows) && fabricInfoRows.every(row => row.qcReady > 0)
      : fabricInfoRows.every(row => row.qcReady > 0);
  const showShrinkageSetting = tabIdx === 1 ? enableRunShrinkage : groupOrder?.state === OState.SHRINKAGE_GROUPING_COMPLETED.state;

  const parentKey = flowInfoRows.find(e => e.key === selectedKey)?.parentKey;

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  const onFabricTableSubmit = (values, form, onSubmitCompleted) => {
    if (enableRunShrinkage) {
      runShrinkageGrouping(formValues, null, null, values);
    } else {
      runFinalGrouping(formValues, null, null, values);
    }
    onSubmitCompleted && onSubmitCompleted(values);
  };
  const warehouses = getDistinctValues(shadeRows.filter(e => !isEmpty(e.whCode)).map(e => e.whCode));
  const finalShadeRows = shadeRows.some(e => e.shadeReady >= 0.0) || warehouses?.length > 1 ? shadeRows : [];

  const hasGroupingPrivilege =
    hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.WRITE, "shrinkage") ||
    hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.WRITE, "final");

  const hasManualPattern = useDeepMemo(() => {
    if (tabIdx === 0) {
      const rollList = rollInfoList?.filter(e => e.groupOrderId === groupOrder?.id);
      return rollList.some(e => !isEmpty(e.warpPattern) && !isEmpty(e.weftPattern));
    } else {
      const rollList = fabricInfoRows?.flatMap(e => e.availableRollList || []) || [];
      return rollList.some(e => !isEmpty(e.warpPattern) && !isEmpty(e.weftPattern));
    }
  }, [tabIdx, fabricInfoRows, rollInfoList, groupOrder]);

  const settingColumns = useDeepMemo(() => {
    let cols = [...groupingSettingFields, "groupingWastage"].map(dataKey => ({
      dataKey,
      title: dataKey,
      render: value => (["warpInterval", "weftInterval", "widthWastePercent"].includes(dataKey) && !isEmpty(value) ? `${value}%` : value)
    }));
    if (!isWashProgram || hasManualPattern) {
      cols = cols.filter(c => !["warpInterval", "weftInterval"].includes(c.dataKey));
    }
    return cols;
  }, [isWashProgram, hasManualPattern]);

  const inlineActions = [{ name: "view", tooltip: "View Rolls", align: "center", icon: <ViewIcon /> }];

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    setDrawerState({ open: true, type: "flatView", group: row, flatView: true });
  };

  if (isEmpty(order)) {
    return <div />;
  }

  return (
    <div>
      <RollDrawer
        open={drawerState.open || false}
        type={drawerState.type}
        group={drawerState.group}
        rollInfoList={rollInfoList}
        groupList={drawerState.groupList}
        flatView={drawerState.flatView}
        onDrawerClose={() => setDrawerState({ open: false })}
      />

      <ExternalRollDrawer
        open={extDrawerState.open || false}
        availableRollList={extDrawerState?.availableRollList || []}
        extAvailableRollList={extRollList}
        ledgerList={ledgerList}
        conversionFactorList={conversionFactorList}
        onDrawerClose={() => setExtDrawerState({ open: false })}
      />

      <div className={classes.content}>
        <Tabs style={{ margin: "1.5em 0" }} value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
          <SubTab label="Group Order" />
          <SubTab label="Ready to Group" />
        </Tabs>

        {/** Error Message */}
        {!isEmpty(errorMessage) && (
          <Alert variant="filled" severity="error" style={{ marginBottom: "1.5em" }}>
            <Typography variant="h6">{errorMessage}</Typography>
          </Alert>
        )}

        {/* Order Details */}
        <MuiTable
          key={version}
          title="Order Details"
          toolbar
          columns={flowInfoColumns}
          rows={flowInfoRows}
          searchable={true}
          searchKeys={["flowRef", "customerFlowRef", "style", "color"]}
          pageable={true}
          selectAll={false}
          selectable={tabIdx === 0 || !hasGroupingPrivilege ? false : row => !row.isParent}
          onSelect={onSelect}
          idKey="key"
          parentIdKey="parentKey"
          defaultExpanded={defaultExpanded}
          onRowClick={onRowClick}
          rowStyle={({ row }) => ({
            backgroundColor:
              tabIdx === 1
                ? selectedKeys.includes(row?.key)
                  ? "#BFDDF8"
                  : "none"
                : row?.key === selectedKey
                ? "#BFDDF8"
                : row?.key === parentKey
                ? "#C6D0DA"
                : "none"
          })}
          i18nKey="resources.grouping.fields.flowInfo"
          emptyMessage="No records available. Hint: Check if fabric association is done and Factory Production order is created."
        />

        {/* Fabric Details */}
        {tabIdx === 1 && !isEmpty(selectedKeys) && isEmpty(errorMessage) && (
          <div className={classes.mrTop}>
            <MuiTable
              component="div"
              title="Fabric Details"
              toolbar
              columns={fabricInfoColumns}
              rows={fabricInfoRows}
              i18nKey="resources.grouping.fields.flowInfo"
              editable={true}
              editing={true}
              handleSubmitRef={handleSubmitRef}
              onSubmit={onFabricTableSubmit}
            />
          </div>
        )}

        {tabIdx === 0 && !isEmpty(groupOrder) && (
          <div className={classes.mrTop}>
            <MuiTable
              component="div"
              title="Fabric Details"
              toolbar
              columns={fabricInfoColumns}
              rows={fabricInfoRows}
              cellOverFlow="wrap"
              i18nKey="resources.grouping.fields.flowInfo"
            />
          </div>
        )}

        {/* Shade Allocation */}
        {tabIdx === 1 && !isEmpty(selectedKeys) && !isEmpty(finalShadeRows) && isEmpty(errorMessage) && (
          <Card className={classes.mrTop}>
            <CardHeader
              title={shadeColumns.some(c => c.dataKey === "warehouse") ? "Warehouse & Shade Allocation" : "Shade Allocation"}
              titleTypographyProps={{ variant: "h6" }}
            />
            <Divider />
            <PerfectScrollbar>
              <Table
                columns={shadeColumns}
                rows={finalShadeRows}
                rowRenderer={ShadeRowRenderer}
                i18nKey="resources.grouping.fields.shadeAllocation"
              />
            </PerfectScrollbar>
          </Card>
        )}

        <WithReduxForm
          initialValues={{
            widthWastePercent: 0.6,
            fabAllocPlusValue: isEmpty(fabricAllocPositiveTolerance) ? undefined : Number(fabricAllocPositiveTolerance),
            fabAllocMinusValue: isEmpty(fabricAllocNegativeTolerance) ? undefined : Number(fabricAllocNegativeTolerance),
            minRollSplitQty: isEmpty(minRollSplitLength) ? undefined : Number(minRollSplitLength),
            ...settings
          }}>
          {({ handleSubmit }) => (
            <div className={classes.mrTop}>
              {!isEmpty(flowInfoRows) && (tabIdx === 1 || !isEmpty(groupOrder)) && (
                <Card>
                  <CardHeader title="Settings" titleTypographyProps={{ variant: "h6" }} />
                  <Divider />
                  {(tabIdx === 1 || groupOrder?.stateValue < OState.FINAL_GROUPING_COMPLETED.stateValue) && (
                    <SimpleForm component="div" style={{ padding: "1.5em" }} footer={false} i18nKey={i18nKey}>
                      {groupingSettingFields.includes("widthWastePercent") && enableRunFinalBtn && (
                        <NumberInput source="widthWastePercent" {...inputOptions(2)} />
                      )}
                      {groupingSettingFields.includes("warpInterval") && showShrinkageSetting && !hasManualPattern && (
                        <NumberInput
                          source="warpInterval"
                          validate={[required(), minValue(0)]}
                          disabled={!isEmpty(shrinkageOptions) || !isEmpty(groupOrder)}
                          {...inputOptions(2)}
                        />
                      )}
                      {groupingSettingFields.includes("weftInterval") && showShrinkageSetting && !hasManualPattern && (
                        <NumberInput
                          source="weftInterval"
                          validate={[required(), minValue(0)]}
                          disabled={!isEmpty(shrinkageOptions) || !isEmpty(groupOrder)}
                          {...inputOptions(2)}
                        />
                      )}
                      {groupingSettingFields.includes("fabAllocPlusValue") && (
                        <NumberInput
                          source="fabAllocPlusValue"
                          validate={[minValue(0)]}
                          disabled={!isEmpty(shrinkageOptions) || !isEmpty(groupOrder)}
                          {...inputOptions(2)}
                        />
                      )}
                      {groupingSettingFields.includes("fabAllocMinusValue") && (
                        <NumberInput
                          source="fabAllocMinusValue"
                          validate={[maxValue(0)]}
                          disabled={!isEmpty(shrinkageOptions) || !isEmpty(groupOrder)}
                          {...inputOptions(2)}
                        />
                      )}
                      {/* {( groupingSettingFields.includes("minRollSplitQty")) && (
                        <NumberInput
                          source="minRollSplitQty"
                          validate={[minValue(0)]}
                          disabled={!isEmpty(shrinkageOptions) || !isEmpty(groupOrder)}
                          {...inputOptions(2)}
                        />
                      )} */}
                    </SimpleForm>
                  )}
                  {groupOrder?.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue && (
                    <MuiTable columns={settingColumns} rows={[settings]} i18nKey="resources.grouping.fields" />
                  )}
                </Card>
              )}

              {/* Shrinkage Group Options*/}
              {!isEmpty(shrinkageOptions) && (
                <Card className={classes.mrTop}>
                  <CardHeader title="Shrinkage Group Options" titleTypographyProps={{ variant: "h6" }} />

                  <GroupOptionsTable
                    type="shrinkage"
                    columns={shrOptionColumns}
                    groupOptions={shrinkageOptions.sort((a, b) => b.groupList.length - a.groupList.length)}
                    handleSelection={handleSelection(handleSubmit)}
                    onDeviationClick={handleDeviationClick}
                    onViewClick={handleViewClick}
                  />
                </Card>
              )}

              {!isEmpty(groupOrder) && groupOrder.state === OState.SHRINKAGE_GROUPING_COMPLETED.state && (
                <div className={classes.mrTop}>
                  <MuiTable
                    title="Shrinkage Groups"
                    columns={shrGroupColumns}
                    rows={shrGroupRows}
                    // component="div"
                    toolbar
                    editable={hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.WRITE, "shrinkage")}
                    footerActions={footerActions}
                    onFooterActionClick={onFooterActionClick}
                    inlineActions={inlineActions}
                    onInlineActionClick={onInlineActionClick}
                    onSubmit={updatePattern}
                  />
                </div>
              )}

              {/* Width Group Options*/}
              {!isEmpty(widthOptions) && (
                <Card className={classes.mrTop}>
                  <CardHeader title="Width Group Options" titleTypographyProps={{ variant: "h6" }} />

                  <GroupOptionsTable
                    type="final"
                    columns={widthOptionColumns}
                    groupOptions={widthOptions}
                    handleSelection={handleSelection(handleSubmit)}
                  />
                </Card>
              )}

              {/* Final Groups changing on width option selection */}
              {!isEmpty(finalOption) && (
                <Card className={classes.mrTop}>
                  <CardHeader title="Final Groups" titleTypographyProps={{ variant: "h6" }} />
                  {/* <Divider /> */}
                  <Table
                    classes={{ table: classes.table }}
                    columns={finalOptionColumns}
                    rows={finalOption.groupList
                      .sort(cbGroupSort)
                      .map(group => ({ ...group, fabricQty: getGroupFabricQty(group, uom, conversionFactorList) }))}
                  />
                </Card>
              )}

              {/* Final Groups */}
              {groupOrder && groupOrder.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue && (
                <div className={classes.mrTop}>
                  <MuiTable
                    title="Final Groups"
                    columns={finalGroupColumns}
                    rows={finalGroupRows}
                    toolbar
                    editable={
                      isWashProgram &&
                      groupOrder.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue &&
                      hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.UPDATE)
                    }
                    footerActions={footerActions}
                    onFooterActionClick={onFooterActionClick}
                    inlineActions={inlineActions}
                    onInlineActionClick={onInlineActionClick}
                    onSubmit={updatePattern}
                  />
                </div>
              )}

              {/* Grouping Buttons */}
              {(!isEmpty(shrinkageOptions) || !isEmpty(widthOptions)) && (
                <div className={classes.buttons}>
                  <Button label="Save" color="primary" variant="contained" style={{ margin: "0 1em" }} disabled={busy} onClick={onSave} />
                  <Button label="Cancel" color="primary" variant="outlined" style={{ margin: "0 1em" }} onClick={onCancel} />
                </div>
              )}
              {!isEmpty(flowInfoRows) && isEmpty(shrinkageOptions) && isEmpty(widthOptions) && tabIdx === 1 && (
                <div className={classes.buttons}>
                  {hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.WRITE, "shrinkage") && (
                    <Button
                      label="Run Shrinkage Grouping"
                      color="primary"
                      variant="contained"
                      style={{ margin: "0 1em" }}
                      disabled={!enableRunShrinkage || busy}
                      onClick={event => {
                        handleSubmit(() => {})();
                        ffHandleSubmit(event);
                      }}
                    />
                  )}
                  {hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.WRITE, "final") && (
                    <Button
                      label="Run Final Grouping"
                      color="primary"
                      variant="contained"
                      style={{ margin: "0 1em" }}
                      disabled={!enableRunFinalBtn || busy}
                      onClick={event => {
                        handleSubmit(() => {})();
                        ffHandleSubmit(event);
                      }}
                    />
                  )}
                </div>
              )}
              {isEmpty(widthOptions) && !isEmpty(groupOrder) && tabIdx === 0 && (
                <div className={classes.buttons}>
                  {groupOrder.state === OState.SHRINKAGE_GROUPING_COMPLETED.state &&
                    hasPrivilege(roles, hasAccess, ResourceId.GROUPING, Scope.WRITE, "final") && (
                      <Button
                        label="Run Final Grouping"
                        color="primary"
                        variant="contained"
                        style={{ margin: "0 1em" }}
                        disabled={!enableRunFinalBtn || busy}
                        onClick={handleSubmit(runFinalGrouping)}
                      />
                    )}
                </div>
              )}
            </div>
          )}
        </WithReduxForm>
      </div>

      <PageFooter style={{ paddingLeft: "1.5em", marginTop: 0, paddingTop: 0 }}>
        <BackButton variant="outlined" />
      </PageFooter>
    </div>
  );
};

export default GroupingView;
